import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ChangeEmailBody,
  ChangePasswordBody,
  DeleteAccountBody,
  ForgotPasswordBody,
  LoginBody,
  MeResponse,
  ProfileUpdateBody,
  ResetPasswordBody,
  SignupBody,
} from "@guestbook/shared";
import { apiFetch, ApiClientError } from "@/shared/lib/api";

const ME_KEY = ["me"] as const;

/** Fire the refresh this many ms before the access cookie expires. */
const REFRESH_LEAD_MS = 5 * 60 * 1000;

/** Half-width of the uniform jitter applied to the refresh fire time. */
const REFRESH_JITTER_HALF_MS = 30 * 1000;

/** Minimum delay before firing; prevents a near-instant loop if expiresAt is
 *  already close or in the past on mount. */
const MIN_DELAY_MS = 1000;

export function useMe() {
  return useQuery<MeResponse | null, ApiClientError>({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await apiFetch<MeResponse>("/auth/me");
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 401) {
          return null;
        }
        throw err;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation<MeResponse, ApiClientError, LoginBody>({
    mutationFn: (body) =>
      apiFetch<MeResponse>("/auth/login", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (user) => {
      qc.setQueryData(ME_KEY, user);
    },
  });
}

export function useSignup() {
  const qc = useQueryClient();
  return useMutation<MeResponse, ApiClientError, SignupBody>({
    mutationFn: (body) =>
      apiFetch<MeResponse>("/auth/signup", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (user) => {
      qc.setQueryData(ME_KEY, user);
    },
  });
}

/** Request a password-reset email. Resolves on the generic 204 regardless of
 *  whether the email matched an account (the server never reveals which). */
export function useRequestPasswordReset() {
  return useMutation<void, ApiClientError, ForgotPasswordBody>({
    mutationFn: (body) =>
      apiFetch<void>("/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  });
}

/** Redeem a reset token and set a new password. On success the user is sent to
 *  login (the server mints no session), so this doesn't touch the me-cache. */
export function useResetPassword() {
  return useMutation<void, ApiClientError, ResetPasswordBody>({
    mutationFn: (body) =>
      apiFetch<void>("/auth/reset-password", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, void>({
    mutationFn: () => apiFetch<void>("/auth/logout", { method: "POST" }),
    onSuccess: () => {
      qc.setQueryData(ME_KEY, null);
      // Drop any user-scoped cached data so a different account
      // doesn't briefly see the previous user's content.
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
    },
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation<MeResponse, ApiClientError, ProfileUpdateBody>({
    mutationFn: (body) =>
      apiFetch<MeResponse>("/auth/profile", {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: (user) => {
      qc.setQueryData(ME_KEY, user);
    },
  });
}

export function useChangeEmail() {
  const qc = useQueryClient();
  return useMutation<MeResponse, ApiClientError, ChangeEmailBody>({
    mutationFn: (body) =>
      apiFetch<MeResponse>("/auth/email", {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: (user) => {
      qc.setQueryData(ME_KEY, user);
    },
  });
}

export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation<MeResponse, ApiClientError, ChangePasswordBody>({
    mutationFn: (body) =>
      apiFetch<MeResponse>("/auth/password", {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: (user) => {
      qc.setQueryData(ME_KEY, user);
    },
  });
}

export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, DeleteAccountBody>({
    mutationFn: (body) =>
      apiFetch<void>("/auth/account", {
        method: "DELETE",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.setQueryData(ME_KEY, null);
      // Drop any user-scoped cached data so a different account
      // doesn't briefly see the previous user's content.
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
    },
  });
}

/**
 * Compute the delay until the next proactive refresh fire. Exported for tests.
 * `random` is injected so tests can pin jitter; production passes Math.random.
 */
export function computeRefreshDelayMs(
  expiresAtMs: number,
  nowMs: number,
  random: () => number = Math.random,
): number {
  const targetMs = expiresAtMs - REFRESH_LEAD_MS;
  // Jitter is symmetric around the target: (random - 0.5) * 2 * half-width.
  const jitter = (random() - 0.5) * 2 * REFRESH_JITTER_HALF_MS;
  return Math.max(MIN_DELAY_MS, targetMs + jitter - nowMs);
}

/**
 * Schedules a single setTimeout that calls /auth/refresh at
 * (expiresAt − 5 min) ± 30 s, then reschedules from the new expiresAt.
 *
 * Read `expiresAt` from the cached ["me"] entry. We re-read inside the timer
 * callback (not just the effect closure) so concurrent updates from the 401
 * fallback path don't leave us scheduling against a stale value.
 *
 * The effect re-runs whenever `expiresAt` changes, which means a successful
 * refresh (mutating the cache) naturally re-schedules.
 */
export function useRefreshTimer(): void {
  const qc = useQueryClient();
  const me = useMe();
  const expiresAt = me.data?.expiresAt;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!expiresAt) return;

    const schedule = (): void => {
      const cached = qc.getQueryData<MeResponse | null>(ME_KEY);
      const ms = cached?.expiresAt ? Date.parse(cached.expiresAt) : NaN;
      if (!Number.isFinite(ms)) return;
      const delay = computeRefreshDelayMs(ms, Date.now());

      timerRef.current = setTimeout(async () => {
        try {
          const fresh = await apiFetch<MeResponse>("/auth/refresh", {
            method: "POST",
          });
          qc.setQueryData(ME_KEY, fresh);
          schedule();
        } catch {
          // Refresh failed — RequireAuth picks this up via the next /auth/me
          // 401 (cached "me" is now stale; React Query will refetch when its
          // staleTime elapses or another mount triggers it). No retry here:
          // we keep this hook narrowly responsible for "fire on schedule."
        }
      }, delay);
    };

    schedule();

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [expiresAt, qc]);
}
